import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, MapPin, Users, MessageCircle, Star, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export default function JoinedRides() {
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchJoinedRides();
  }, []);

  const fetchJoinedRides = async () => {
    try {
      const response = await axios.get('/rides/joined');
      setRides(response.data);
    } catch (error) {
      toast.error('Failed to load rides');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <p className="text-gray-400">Loading joined rides...</p>
      </div>
    );
  }

  if (rides.length === 0) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-400 text-lg mb-4">You haven't joined any rides yet</p>
        <p className="text-gray-500 text-sm">Browse rides and swipe right to join!</p>
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="joined-rides">
      {rides.map((ride) => (
        <Card key={ride.id} data-testid="joined-ride-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg" data-testid="joined-ride-distance">{ride.distance}km Ride</CardTitle>
                {ride.category && (
                  <Badge variant="outline" className="bg-purple-500/10 border-purple-500/30 text-purple-400 text-xs mt-1">
                    {ride.category === 'road' && '🛣️ Road'}
                    {ride.category === 'mountain' && '⛰️ Mountain'}
                    {ride.category === 'gravel' && '🏞️ Gravel'}
                    {ride.category === 'commute' && '🚴 Commute'}
                  </Badge>
                )}
              </div>
              <Badge variant={ride.status === 'completed' ? 'default' : 'secondary'} data-testid="joined-ride-status">
                {ride.status}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-bold">
                  {ride.creator?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground" data-testid="joined-ride-creator">{ride.creator?.name}</p>
                <div className="flex items-center gap-1">
                  <Star className="w-3 h-3 text-yellow-500" fill="currentColor" />
                  <span className="text-xs text-muted-foreground" data-testid="joined-ride-creator-rating">
                    {ride.creator?.avg_rating.toFixed(1)}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-purple-500" />
              <span className="text-muted-foreground" data-testid="joined-ride-datetime">{ride.date} at {ride.time}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <MapPin className="w-4 h-4 text-purple-500" />
              <span className="text-muted-foreground" data-testid="joined-ride-location">{ride.postcode || ride.location}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Users className="w-4 h-4 text-purple-500" />
              <span className="text-muted-foreground" data-testid="joined-ride-participants">
                {ride.participants_count + 1} total riders
              </span>
            </div>
            {ride.route_link && (
              <div className="pt-2">
                <a
                  href={ride.route_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-purple-400 hover:text-purple-300 hover:underline"
                  data-testid="joined-ride-route-link"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>View Route Map</span>
                </a>
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              className="w-full mt-2"
              onClick={() => navigate(`/ride/${ride.id}`)}
              data-testid="view-joined-ride-button"
            >
              <MessageCircle className="w-4 h-4 mr-1" />
              View & Chat
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
